import type { AtHomeCaseloadMember, AtHomeTier } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import type { OpCtx } from './OperatorHome';
import { Act, chip16, D, DASH, S } from './ui';
import { useCustomerText } from '../../ui/buildLabels';
import { logoBg, logoUrl } from '../settings/logos';
import { useBuildLabels } from '../../ui/buildLabels';

// Members (design T11711-11832, hOpCaseload): the caseload table and the eight panels
// under it. Name, check-in, devices and owner are the real GET /athome/caseload; the
// account, readiness, tier, wearable and alert columns are PROPOSED fields on it, and
// the panels read the PROPOSED GET /athome/caseload/panels.

export type MemFilter = 'all' | 'attention' | 'no2fa' | 'onboarding' | 'billing';
const FILTERS: [MemFilter, string][] = [['all', 'All members'], ['attention', 'Needs attention'], ['no2fa', 'No 2FA'], ['onboarding', 'Still onboarding'], ['billing', 'Billing issue']];
const STEPS = ['invited', 'devices shipped', 'set up', 'first check-in', 'day 90'];
const GRID = 'display:grid;grid-template-columns:minmax(150px,1.4fr) 86px minmax(104px,1fr) 84px 78px 96px 66px minmax(104px,.9fr) minmax(150px,1fr) 118px;gap:10px;';

export const tierColor = (t: AtHomeTier | null | undefined) => (t === 'HIGH' ? '#8C3A33' : t === 'ELEVATED' ? '#B3261E' : t === 'WATCH' ? '#5B6B7C' : t === 'LOW' ? '#1F6B52' : '#54606C');
const readyColor = (p: number) => (p >= 85 ? '#1F6B52' : p >= 60 ? '#264C7E' : '#B3261E');
const fmtAge = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
export const stateStyle = (s: string) =>
  'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;' +
  (s === 'ACTIVE' ? 'color:#1F6B52;background:rgba(47,138,111,.14);' : s === 'CLOSING' ? 'color:#4A5866;background:rgba(91,107,124,.16);' : s === DASH ? 'color:#54606C;background:rgba(34,39,46,.07);' : 'color:#B3261E;background:rgba(179,38,30,.12);');

function matches(m: AtHomeCaseloadMember, f: MemFilter): boolean {
  const a = m.account;
  if (f === 'all') return true;
  if (!a) return false;
  if (f === 'attention') return a.state !== 'ACTIVE' || !a.twoFactor || a.onboardingStep < 4 || !!a.billingIssue;
  if (f === 'no2fa') return !a.twoFactor;
  if (f === 'onboarding') return a.onboardingStep < 4;
  return !!a.billingIssue;
}

/** The filter beside the tabs (hMemFilters), with its count per option. */
export function MemberFilter({ c }: { c: OpCtx }) {
  const list = c.o.caseload.data?.members ?? [];
  const off = list.filter((m) => m.account && m.account.state !== 'ACTIVE');
  return (
    <S css="flex:0 0 auto;display:flex;align-items:center;gap:7px;min-width:0;">
      <S css="flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;">{off.map((m) => `${m.name.split(' ')[0]} ${m.account!.state.toLowerCase()}`).join(' · ')}</S>
      <select
        value={c.memFilter}
        aria-label="Show members"
        onChange={(e) => c.setMemFilter(e.target.value as MemFilter)}
        style={sx('flex:0 0 auto;height:28px;max-width:210px;padding:0 8px;border-radius:7px;border:1px solid rgba(34,39,46,.2);font-size:14px;color:#22272E;font-family:inherit;')}
      >
        {FILTERS.map(([v, l]) => (
          <option key={v} value={v}>{`${l} (${list.filter((m) => matches(m, v)).length})`}</option>
        ))}
      </select>
    </S>
  );
}

const PANEL_HEAD = 'display:flex;align-items:baseline;gap:8px;padding-bottom:3px;min-width:0;flex-wrap:wrap;';
const PANEL_TITLE = 'flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:-.01em;color:#22272E;white-space:nowrap;';
const PANEL_META = 'flex:1 1 120px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;';
const ROWLINK = 'display:flex;align-items:center;gap:8px;padding:1px 0;text-decoration:none;color:inherit;';
const shapeMark = (color: string, shape: 'circle' | 'square' | 'diamond') =>
  'display:inline-block;width:9px;height:9px;flex:0 0 9px;background:' + color + ';border-radius:' + (shape === 'circle' ? '50%' : shape === 'square' ? '2px' : '0') + ';' + (shape === 'diamond' ? 'transform:rotate(45deg) scale(.85);' : '');
const TONE_DOT: Record<string, string> = { good: '#2F8A6F', info: '#264C7E', watch: '#5B6B7C', alert: '#B3261E', none: '#54606C' };

/**
 * The wearable maker's mark for the Devices column (design wearLogoStyle), from the device
 * link's provider. A maker with no bundled mark shows its name instead, never a blank.
 */
const MAKER_LOGO: Array<[RegExp, string]> = [
  [/apple/i, 'apple-trim.png'],
  [/samsung/i, 'samsung-trim.png'],
  [/fitbit/i, 'fitbit-trim.png'],
  [/garmin/i, 'garmin-trim.png'],
  [/withings/i, 'withings-trim.png'],
  [/oura/i, 'oura-trim.png'],
  [/whoop/i, 'whoop-trim.png'],
  [/bay[ _-]?alarm/i, 'bayalarm-trim.png'],
  [/vayyar/i, 'vayyar-cropped-trim.png'],
  [/omron/i, 'omron-trim.png'],
  [/polar/i, 'polar-trim.png'],
  [/dexcom/i, 'dexcom-trim.png'],
  [/medical[ _-]?guardian/i, 'medicalguardian-trim.png'],
];
export function makerLogo(provider: string | null | undefined): string | null {
  if (!provider) return null;
  const hit = MAKER_LOGO.find(([re]) => re.test(provider));
  return hit && logoUrl(hit[1]) ? hit[1] : null;
}
/** "apple_watch" as "Apple Watch". */
export function makerName(provider: string): string {
  return provider.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function Panels({ c }: { c: OpCtx }) {
  const plain = useCustomerText();
  const p = c.o.panels.data;
  const list = c.o.caseload.data?.members ?? [];
  const byId = (id: string | null) => list.find((m) => m.residentId === id) ?? null;
  const open = (id: string | null) => {
    const m = byId(id);
    if (m) c.viewAs({ residentId: m.residentId, name: m.name });
  };
  const waiting = c.o.panels.error ? plain('waiting on GET /athome/caseload/panels') : '';
  const eng = p?.engagement;
  const escTot = p ? p.escalations.outcomes.reduce((n, e) => n + e.n, 0) : 0;
  const ESC_COL = ['#2F8A6F', '#264C7E', '#5B6B7C', '#B3261E'];
  const nowH = new Date().getHours();
  return (
    <>
      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:4px 20px;min-width:0;align-items:start;padding-top:8px;margin-top:6px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Mobility drift</S><S css={PANEL_META}>{p ? 'mobility indicator vs own 30-day baseline · biggest movers' : waiting}</S></D>
          {(p?.drift ?? []).map((d, i) => {
            const s = d.series;
            const mn = Math.min(...s) - 3;
            const mx = Math.max(...s) + 3;
            const delta = s[s.length - 1] - s[0];
            const color = ['#B3261E', '#4A5866', '#264C7E'][i] ?? '#264C7E';
            return (
              <Act key={d.residentId} css={ROWLINK} onClick={() => open(d.residentId)}>
                <S css="flex:0 0 108px;font-size:14px;font-weight:600;overflow-wrap:break-word;">{d.name}</S>
                <svg viewBox="0 0 60 16" preserveAspectRatio="none" style={sx('flex:1 1 0;min-width:0;height:16px;')} aria-hidden="true">
                  <polyline points={s.map((v, j) => `${(j * (60 / (s.length - 1))).toFixed(1)},${(16 - ((v - mn) / (mx - mn)) * 16).toFixed(1)}`).join(' ')} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                </svg>
                <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + color + ';white-space:nowrap;'}>{`${delta >= 0 ? '+' : ''}${delta} pts`}</S>
              </Act>
            );
          })}
        </D>
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Atlas engagement</S><S css={PANEL_META}>{p ? 'today · silence is the earliest signal we have' : waiting}</S></D>
          <D css="display:flex;align-items:baseline;gap:8px;">
            <S css="font-size:22px;font-weight:700;letter-spacing:-.03em;line-height:1;color:#22272E;">{eng ? `${eng.talkedToday} of ${eng.total}` : DASH}</S>
            <S css="flex:1 1 0;min-width:0;font-size:14px;color:#54606C;line-height:1.25;">of members talked to Atlas today</S>
          </D>
          {eng &&
            // Quiet means no talk with Atlas: a member can have checked in an hour ago and still be here.
            ([['Talked to Atlas today', eng.talkedToday, '#2F8A6F', 'circle'], ['Last talked to Atlas 1 day ago', eng.quiet1, '#5B6B7C', 'circle'], ['Last talked to Atlas 3+ days ago', eng.quiet3, '#B3261E', 'square']] as const).map(([k, v, col, sh]) => (
              <Act key={k} css={ROWLINK} onClick={() => c.goTab(k === 'Talked to Atlas today' ? 'caseload' : 'alerts')}>
                <S css={shapeMark(col, sh)} />
                <S css="flex:0 1 150px;min-width:0;font-size:14px;font-weight:600;line-height:1.25;">{k}</S>
                <S css="flex:1 1 0;min-width:0;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;"><S css={'display:block;height:100%;width:' + Math.round((v / Math.max(1, eng.total)) * 100) + '%;border-radius:3px;background:' + col + ';'} /></S>
                <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (v && col !== '#2F8A6F' ? col : '#22272E') + ';'}>{String(v)}</S>
              </Act>
            ))}
        </D>
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Care team load</S><S css={PANEL_META}>{p ? 'members owned · open alerts · tonight' : waiting}</S></D>
          {(p?.load ?? []).map((l) => (
            <D key={l.who} css="display:flex;align-items:center;gap:8px;padding:1px 0;">
              <S css="flex:0 0 66px;font-size:14px;font-weight:600;overflow-wrap:break-word;">{l.who}</S>
              <S css="flex:1 1 0;min-width:0;display:flex;height:8px;border-radius:4px;overflow:hidden;background:rgba(34,39,46,.08);">
                <S css={'display:block;height:100%;width:' + Math.round((l.members / 6) * 100) + '%;background:#264C7E;'} />
                <S css={'display:block;height:100%;width:' + Math.round((l.open / 6) * 100) + '%;background:#5B6B7C;'} />
              </S>
              <S css="flex:0 0 auto;font-size:14px;font-variant-numeric:tabular-nums;color:#3C454F;white-space:nowrap;">
                <span>{`${l.members} ${l.members === 1 ? 'member' : 'members'}`}</span>{' '}
                <span style={sx('color:#4A5866;font-weight:700;')}>{l.open ? `${l.open} open` : ''}</span>
              </S>
            </D>
          ))}
          <D css="font-size:14px;color:#54606C;margin-top:4px;line-height:1.35;">{p ? 'Blue is members owned; amber is alerts still open on them. Red is nobody’s.' : ''}</D>
        </D>
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Just now</S><S css={PANEL_META}>{p ? 'live across the caseload · tap to open' : waiting}</S></D>
          {(p?.feed ?? []).map((f, i) => (
            <Act key={i} css="display:flex;align-items:baseline;gap:8px;padding:2px 0;text-decoration:none;color:inherit;" onClick={() => open(f.residentId)}>
              <S css="flex:0 0 38px;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;">{f.at}</S>
              <S css={'display:inline-block;width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (TONE_DOT[f.tone] ?? '#54606C') + ';align-self:center;'} />
              <S css="flex:1 1 0;min-width:0;font-size:14px;line-height:1.3;overflow-wrap:break-word;">
                <b style={sx('font-weight:600;')}>{f.who}</b> {f.what}
              </S>
            </Act>
          ))}
        </D>
      </D>
      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:4px 20px;min-width:0;align-items:start;padding-top:8px;margin-top:6px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Response time</S><S css={PANEL_META}>{p ? 'first human contact after an alert · 7 days · target by tier' : waiting}</S></D>
          <D css="display:flex;align-items:baseline;gap:8px;">
            <S css="font-size:22px;font-weight:700;letter-spacing:-.03em;line-height:1;color:#22272E;">{p ? (p.sla.medianMinutes == null ? DASH : `${p.sla.medianMinutes}m`) : DASH}</S>
            <S css="flex:1 1 0;min-width:0;font-size:14px;color:#54606C;line-height:1.25;">{p && p.sla.medianMinutes == null ? 'no alerts answered yet' : 'typical time to a person'}</S>
          </D>
          {(p?.sla.tiers ?? []).map((r, i) => {
            // No alert in this tier: a dash, never "0m" (absent is not zero).
            const none = r.minutes == null;
            const ok = none || (r.minutes as number) <= r.targetMinutes;
            const shape = (['square', 'circle', 'diamond'] as const)[i] ?? 'circle';
            return (
              <D key={r.tier} css="display:flex;align-items:center;gap:8px;padding:1px 0;">
                <S css={shapeMark(['#B3261E', '#4A5866', '#264C7E'][i] ?? '#264C7E', shape)} />
                <S css="flex:0 0 74px;font-size:14px;font-weight:600;">{r.tier}</S>
                <S css="flex:1 1 0;min-width:0;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;position:relative;">
                  <S css={'position:absolute;left:0;top:0;bottom:0;width:' + (none ? 0 : Math.min(100, Math.round(((r.minutes as number) / (r.targetMinutes * 1.25)) * 100))) + '%;border-radius:3px;background:' + (ok ? '#2F8A6F' : '#B3261E') + ';'} />
                  <S css="position:absolute;top:-2px;bottom:-2px;left:80%;width:2px;background:rgba(34,39,46,.4);" />
                </S>
                <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (none ? '#54606C' : ok ? '#1F6B52' : '#B3261E') + ';'}>{none ? DASH : `${r.minutes}m`}</S>
                <S css="flex:0 0 auto;font-size:14px;color:#54606C;">{'of '}<span>{`${r.targetMinutes}m`}</span></S>
              </D>
            );
          })}
        </D>
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Escalation outcomes</S><S css={PANEL_META}>{p ? `where the ladder stopped · last 7 days · ${escTot} alerts` : waiting}</S></D>
          <S css="display:flex;height:10px;border-radius:5px;overflow:hidden;gap:2px;">
            {(p?.escalations.outcomes ?? []).map((e, i) => (
              <S key={e.k} css={'display:block;height:100%;flex:0 0 ' + Math.round((e.n / Math.max(1, escTot)) * 100) + '%;background:' + ESC_COL[i % 4] + ';'} />
            ))}
          </S>
          <D css="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:4px 12px;margin-top:8px;">
            {(p?.escalations.outcomes ?? []).map((e, i) => (
              <S key={e.k} css="display:flex;align-items:baseline;gap:6px;min-width:0;">
                <S css={'display:inline-block;width:8px;height:8px;border-radius:2px;flex:0 0 8px;background:' + ESC_COL[i % 4] + ';'} />
                <S css="font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;">{String(e.n)}</S>
                <S css="flex:1 1 0;min-width:0;font-size:14px;color:#3C454F;overflow-wrap:break-word;">{e.k}</S>
              </S>
            ))}
          </D>
          <D css="font-size:14px;color:#54606C;margin-top:8px;line-height:1.4;text-wrap:pretty;">{p?.escalations.note ?? ''}</D>
        </D>
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Check-ins by hour</S><S css={PANEL_META}>{p ? 'all members · today vs 7-day norm · grey is the norm' : waiting}</S></D>
          <S css="display:flex;align-items:flex-end;gap:2px;height:26px;">
            {(p?.checkins.today ?? []).map((v, hr) => {
              const norm = p!.checkins.norm[hr] ?? 0;
              const future = hr > (p!.checkins.hour ?? nowH);
              return (
                <S
                  key={hr}
                  title={`${String(hr).padStart(2, '0')}:00 · ${v} check-ins · norm ${norm}`}
                  css={'flex:1 1 0;min-width:0;align-self:flex-end;border-radius:2px 2px 0 0;height:' + Math.max(3, Math.round(((future ? norm : v) / 14) * 100)) + '%;background:' + (future ? 'rgba(34,39,46,.10)' : v < norm * 0.6 && norm >= 4 ? '#5B6B7C' : '#2F8A6F') + ';'}
                />
              );
            })}
          </S>
          <S css="display:flex;justify-content:space-between;font-size:14px;color:#54606C;margin-top:3px;">
            <span>00</span><span>06</span><span>12</span><span>18</span><span>now</span>
          </S>
          <D css="display:flex;gap:12px;flex-wrap:wrap;margin-top:5px;">
            <S css="font-size:14px;color:#3C454F;"><b style={sx('font-weight:700;')}>{p ? String(p.checkins.total) : DASH}</b> today</S>
            <S css="font-size:14px;color:#3C454F;"><b style={sx('font-weight:700;')}>{p ? String(p.checkins.missedWindows) : DASH}</b> missed windows</S>
            <S css="font-size:14px;color:#3C454F;"><b style={sx('font-weight:700;')}>{p ? p.checkins.overnightQuiet : DASH}</b> overnight quiet</S>
          </D>
        </D>
        <D css="min-width:0;">
          <D css={PANEL_HEAD}><S css={PANEL_TITLE}>Falls {'·'} 30 days</S><S css={PANEL_META}>{p ? 'detected by watch or pendant · all members' : waiting}</S></D>
          <D css="display:flex;align-items:baseline;gap:14px;flex-wrap:wrap;">
            {p &&
              ([[p.falls.falls, 'falls', '#22272E'], [p.falls.injuries, 'injuries', '#1F6B52'], [p.falls.dispatches, '911 dispatch', '#B3261E']] as const).map(([v, k, col]) => (
                <S key={k} css="display:block;">
                  <S css={'display:block;font-size:20px;font-weight:700;letter-spacing:-.03em;line-height:1;color:' + col + ';'}>{String(v)}</S>
                  <S css="display:block;font-size:14px;font-weight:600;color:#54606C;margin-top:2px;">{k}</S>
                </S>
              ))}
          </D>
          <S css="display:flex;align-items:flex-end;gap:2px;height:20px;margin-top:5px;">
            {(p?.falls.days ?? []).map((d, i, all) => (
              <S key={i} title={`${all.length - i} days ago · ${d.falls} fall · ${d.nearMisses} near-miss`} css={'flex:1 1 0;min-width:0;align-self:flex-end;border-radius:2px 2px 0 0;height:' + (d.falls ? 100 : d.nearMisses ? 22 + d.nearMisses * 18 : 6) + '%;background:' + (d.falls ? '#B3261E' : d.nearMisses ? '#5B6B7C' : 'rgba(34,39,46,.10)') + ';'} />
            ))}
          </S>
          <S css="display:flex;justify-content:space-between;font-size:14px;color:#54606C;margin-top:3px;"><span>30d ago</span><span>today</span></S>
        </D>
      </D>
    </>
  );
}

export function OpMembers({ c }: { c: OpCtx }) {
  const buildLabels = useBuildLabels();
  const cl = c.o.caseload.data;
  const list = (cl?.members ?? []).filter((m) => matches(m, c.memFilter));
  const un = (m: AtHomeCaseloadMember) => m.alerts?.unowned ?? 0;
  const rows = list.slice().sort((a, b) => un(b) - un(a) || (b.lastCheckInMinutes ?? Infinity) - (a.lastCheckInMinutes ?? Infinity));
  return (
    <D css="display:grid;grid-template-columns:minmax(0,1fr);gap:4px;min-width:0;align-items:start;">
      <D css="min-width:0;overflow-x:auto;">
        <D css="min-width:1160px;">
          {/* Headers wrap inside their own column and sit on one baseline at the bottom, so a
              long one (Readiness with its "modeled" tag) never runs into the next. */}
          <D testId="caseload-head" css={GRID + 'align-items:end;overflow-wrap:anywhere;font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:normal;line-height:1.25;color:#54606C;font-weight:600;padding:0 8px 6px;border-bottom:1px solid rgba(34,39,46,.12);'}>
            <span>Member</span>
            <span>State</span>
            <span style={sx('display:flex;flex-direction:column;')}>
              <span>Readiness</span>
              <span style={sx('letter-spacing:0;text-transform:none;color:#8A929A;')}>{buildLabels ? 'modeled' : 'est.'}</span>
            </span>
            <span>Check-in</span>
            <span>Mobility</span>
            <span>Devices</span>
            <span style={sx('text-align:right;')}>Alerts</span>
            <span>Owner</span>
            <span>Plan &amp; setup</span>
            <span />
          </D>
          {rows.map((m, ci) => {
            const a = m.account ?? null;
            const state = a?.state ?? DASH;
            const u = un(m);
            const open = m.alerts?.open ?? null;
            const owner = u ? '' : open ? m.alerts?.owner ?? m.owner : m.owner;
            const r = m.readiness ?? null;
            const mins = m.lastCheckInMinutes;
            const full = m.devicesLinked > 0 && m.devicesReporting === m.devicesLinked;
            const tc = tierColor(m.tier);
            const planLabel = a ? `${a.plan} · $${a.priceUsd}` + (a.billingIssue ? ` · ${a.billingIssue}` : '') + (a.twoFactor ? '' : ' · no 2FA') : DASH;
            const planColor = a?.billingIssue ? '#B3261E' : a?.twoFactor === false ? '#4A5866' : '#3C454F';
            const step = a?.onboardingStep ?? null;
            return (
              <D
                key={m.residentId}
                testId="caseload-row"
                css={'animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(300, ci * 24) + 'ms;' + GRID + 'align-items:center;padding:3px 8px;border-bottom:1px solid rgba(34,39,46,.06);border-radius:6px;' + (u ? 'background:rgba(179,38,30,.05);' : '')}
              >
                <S css="min-width:0;display:flex;align-items:baseline;gap:6px;">
                  <S css="font-size:16px;font-weight:600;overflow-wrap:break-word;">{m.name}</S>
                  <S css="font-size:14px;color:#54606C;white-space:nowrap;">{m.place ?? ''}</S>
                </S>
                <S css={stateStyle(state)}>{state}</S>
                <S css="display:flex;align-items:center;gap:6px;min-width:0;">
                  <S css="flex:1 1 0;min-width:0;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;">
                    <S css={'display:block;height:100%;width:' + (r ?? 0) + '%;border-radius:3px;background:' + (r == null ? 'transparent' : readyColor(r)) + ';'} />
                  </S>
                  <S css={'flex:0 0 32px;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (r == null ? '#54606C' : readyColor(r)) + ';'}>{r == null ? DASH : `${r}%`}</S>
                </S>
                <S css={'font-size:14px;font-variant-numeric:tabular-nums;color:' + (mins == null || mins > 180 ? '#B3261E' : mins > 90 ? '#4A5866' : '#22272E') + ';white-space:nowrap;'}>
                  {mins == null ? 'never' : `${fmtAge(mins)} ago`}
                </S>
                <S css={chip16(tc, tc + '22')}>{m.tier ? (m.tier === 'HIGH' ? '● ' : m.tier === 'ELEVATED' ? '◐ ' : '○ ') + m.tier : DASH}</S>
                <S
                  css="display:flex;align-items:center;gap:6px;min-width:0;"
                  title={`${m.wearable?.name ?? 'No wearable on file'}${m.wearable ? ` · ${m.wearable.fallDetection}` : ''} · ${m.devicesReporting} of ${m.devicesLinked} devices reporting`}
                >
                  {(() => {
                    const logo = makerLogo(m.wearable?.name);
                    if (logo) return <span role="img" translate="no" aria-label={makerName(m.wearable!.name)} style={sx('flex:0 0 56px;width:56px;height:14px;background-image:' + logoBg(logo) + ';background-size:contain;background-repeat:no-repeat;background-position:left center;opacity:.9;')} />;
                    return m.wearable ? <span translate="no" style={sx('flex:0 1 auto;min-width:0;font-size:14px;font-weight:600;color:#3C454F;line-height:1.2;overflow-wrap:anywhere;')}>{makerName(m.wearable.name)}</span> : <S css="flex:0 0 56px;width:56px;height:14px;" label="No wearable on file" />;
                  })()}
                  <S css={'flex:0 0 auto;font-size:14px;font-variant-numeric:tabular-nums;color:' + (full ? '#1F6B52' : '#4A5866') + ';white-space:nowrap;'}>{`${full ? '● ' : '◐ '}${m.devicesReporting}/${m.devicesLinked}`}</S>
                </S>
                <S css={'text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (u ? '#B3261E' : open ? '#22272E' : '#54606C') + ';'}>{open == null ? DASH : String(open)}</S>
                {/* Nobody on an open alert, or no caregiver assigned at all (owner null on the real caseload): both are UNOWNED. */}
                <S css={'font-size:14px;color:' + (u || !owner ? '#B3261E' : '#3C454F') + ';white-space:nowrap;'}>{u || !owner ? 'UNOWNED' : owner}</S>
                <S css="min-width:0;" title={a ? `${a.plan} · $${a.priceUsd}${a.billingIssue ? ` · ${a.billingIssue}` : ''} · 2FA ${a.twoFactor ? 'on' : 'off'} · care circle ${a.careCircle} · ${STEPS[a.onboardingStep]}` : undefined}>
                  <S css={'display:block;font-size:14px;color:' + planColor + ';overflow-wrap:break-word;'}>{planLabel}</S>
                  <S css="display:flex;align-items:center;gap:6px;margin-top:2px;min-width:0;">
                    <S css="flex:1 1 0;min-width:0;height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;">
                      <S css={'display:block;height:100%;width:' + ((step ?? 0) / 4) * 100 + '%;border-radius:3px;background:' + (step === 4 ? '#2F8A6F' : '#5B6B7C') + ';'} />
                    </S>
                    <S css={'flex:0 0 auto;font-size:14px;color:' + (step === 4 ? '#54606C' : '#4A5866') + ';white-space:nowrap;'}>{step == null ? '' : STEPS[step]}</S>
                  </S>
                </S>
                <S css="display:flex;gap:5px;justify-content:flex-end;">
                  <Act css="display:inline-flex;align-items:center;justify-content:center;height:24px;padding:0 8px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;color:#3C454F;background:rgba(34,39,46,.07);white-space:nowrap;" onClick={() => c.manage(m.residentId)}>
                    Manage
                  </Act>
                  <Act css="display:inline-flex;align-items:center;justify-content:center;height:24px;padding:0 8px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;color:#2F5C97;border:1px solid rgba(47,92,151,.35);white-space:nowrap;" onClick={() => c.viewAs({ residentId: m.residentId, name: m.name })}>
                    View as
                  </Act>
                </S>
              </D>
            );
          })}
          {cl && rows.length === 0 && (
            <D css="padding:10px 8px;font-size:16px;color:#54606C;">{cl.scoped ? 'No members match this filter.' : 'No at-home members in this session’s organization. The caseload is scoped to your organization by the server.'}</D>
          )}
        </D>
      </D>
      <D css="flex:0 0 auto;font-size:14px;color:#54606C;line-height:1.35;text-wrap:pretty;margin-top:4px;">
        {buildLabels
          ? 'Sorted unowned alerts first, then longest since check-in. Readiness is the four-pillar checklist score and is MODELED. It is a plan, not a measurement. ● ok · ◐ partial · ○ low.'
          : 'Sorted unowned alerts first, then longest since check-in. Readiness is the four-pillar checklist score: a plan, not a measurement. ● ok · ◐ partial · ○ low.'}
      </D>
      <Panels c={c} />
    </D>
  );
}
